import {
  Badge,
  confirm,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import {
  CopyIcon,
  EditIcon,
  EyeIcon,
  EyeOffIcon,
  TrashIcon,
} from "lucide-react";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import type { SOPListItemDTO } from "@/modules/sop/domain/contracts";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";

type SOPRow = SOPListItemDTO;

type SOPTableProps = {
  data: SOPRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onView: (sop: SOPRow) => void;
  onEdit: (sop: SOPRow) => void;
  onDuplicate: (sop: SOPRow) => void;
  onToggleStatus: (sop: SOPRow) => void;
  onDelete: (sop: SOPRow) => void;
};

export function SOPTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onView,
  onEdit,
  onDuplicate,
  onToggleStatus,
  onDelete,
}: SOPTableProps) {
  const { has } = usePermissions();

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return (
          <Badge variant="default" className="bg-green-100 text-green-800">
            ໃຊ້ງານ
          </Badge>
        );
      case "draft":
        return <Badge variant="secondary">ຮ່າງ</Badge>;
      case "inactive":
        return <Badge variant="destructive">ບໍ່ໃຊ້ງານ</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const columns: TanstackReactTable.ColumnDef<SOPRow>[] = [
    createSortableColumn<SOPRow>("title", "ຊື່ SOP", { size: 200 }),
    {
      id: "departmentName",
      header: "ພະແນກທີ່ກ່ຽວຂ້ອງ",
      cell: ({ row }) => (
        <span className="text-sm">{row.original.departmentName || "-"}</span>
      ),
      size: 150,
    },
    {
      id: "purpose",
      header: "ໃຊ້ກັບວຽກຫຍັງ",
      cell: ({ row }) => (
        <span
          className="max-w-xs truncate text-sm"
          title={row.original.purpose || ""}
        >
          {row.original.purpose || "-"}
        </span>
      ),
      size: 200,
    },
    {
      id: "positionTags",
      header: "Tags",
      cell: ({ row }) => (
        <div className="flex max-w-xs flex-wrap gap-1">
          {row.original.positionTags?.slice(0, 3).map((tag, index) => (
            <Badge key={index} variant="outline" className="text-xs">
              {tag.positionName}
            </Badge>
          ))}
          {row.original.positionTags &&
            row.original.positionTags.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{row.original.positionTags.length - 3}
              </Badge>
            )}
        </div>
      ),
      size: 150,
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => getStatusBadge(row.original.status),
      size: 100,
    },
    {
      id: "updatedAt",
      header: "ວັນທີອັບເດດລ່າສຸດ",
      cell: ({ row }) => (
        <span className="text-sm">
          {formatDateTimeLocal(row.original.updatedAt)}
        </span>
      ),
      size: 120,
    },
    {
      size: 120,
      id: "actions",
      cell: ({ row }: { row: { original: SOPRow } }) => {
        const actions: {
          label: string;
          icon?: React.ReactNode;
          onClick: () => void;
          variant?: "destructive";
        }[] = [];

        if (has(Permissions.sop.read)) {
          actions.push({
            label: "ເບິ່ງລາຍລະອຽດ",
            icon: <EyeIcon className="h-4 w-4" />,
            onClick: () => onView(row.original),
          });
        }

        if (has(Permissions.sop.update)) {
          actions.push({
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(row.original),
          });

          actions.push({
            label: "ຄັດລອກ",
            icon: <CopyIcon className="h-4 w-4" />,
            onClick: () => onDuplicate(row.original),
          });

          // Toggle status action
          const isActive = row.original.status === "active";
          actions.push({
            label: isActive ? "ປິດການໃຊ້ງານ" : "ເປີດການໃຊ້ງານ",
            icon: isActive ? (
              <EyeOffIcon className="h-4 w-4" />
            ) : (
              <EyeIcon className="h-4 w-4" />
            ),
            onClick: async () => {
              const action = isActive ? "ປິດການໃຊ້ງານ" : "ເປີດການໃຊ້ງານ";
              const ok = await confirm({
                title: `${action} SOP`,
                description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະ${action.toLowerCase()} SOP "${row.original.title}"?`,
                actionText: action,
              });
              if (ok) {
                onToggleStatus(row.original);
              }
            },
          });
        }

        if (has(Permissions.sop.delete)) {
          actions.push({
            label: "ລຶບ",
            variant: "destructive",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: "ລຶບ SOP",
                description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບ SOP "${row.original.title}"? ການກະທໍານີ້ບໍ່ສາມາດຍົກເລີກໄດ້.`,
                actionText: "ລຶບ",
                ActionProps: {
                  variant: "destructive",
                },
              });
              if (ok) {
                onDelete(row.original);
              }
            },
          });
        }

        return <RowActions actions={actions} />;
      },
    },
  ];

  return (
    <DataTable<SOPRow, unknown>
      noDataMessage="ບໍ່ພົບ SOP"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
    />
  );
}
